import { Archive, ArchiveRestore, Copy, GitFork, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Collection } from '@/types/collection';
import type { Profile } from '@/types/profile';

import CollectionMark, { markColor } from '@/components/collections/collection-mark';
import CollectionModal, { type CollectionDraft } from '@/components/collections/collection-modal';
import DeleteCollectionModal from '@/components/collections/delete-collection-modal';
import DashboardHeader from '@/components/dashboard/dashboard-header';
import Toast from '@/components/toast';
import { Button } from '@/components/ui/button';
import { POPUP_LEVEL } from '@/components/ui/popup-level';
import { api } from '@/lib/api';



type SortKey = 'updated' | 'title' | 'size';

export default function CollectionsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aff, setAff] = useState<string>('all'); // 'all' | 'personal' | <affiliationId>
  const [lifecycle, setLifecycle] = useState<'active' | 'archived'>('active');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('updated');
  const [menuId, setMenuId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; collection?: Collection } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Collection | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'info' | 'error' } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const showToast = (msg: string, type: 'info' | 'error' = 'info') => setToast({ msg, type });

  useEffect(() => {
    api.get<Collection[]>('/collections')
      .then(setCollections)
      .catch(() => setError(t('collections.errors.couldNotLoad')))
      .finally(() => setLoading(false));
    api.get<Profile>('/profile').then(setProfile).catch(() => {});
  }, []);

  useEffect(() => {
    if (!menuId) return;
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuId(null);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuId]);

  const activeAffiliations = useMemo(() => (profile?.affiliations ?? []).filter((a) => a.active !== false), [profile]);
  const titleById = useMemo(() => new Map(collections.map((c) => [c.id, c.title])), [collections]);
  const affName = useMemo(() => new Map(activeAffiliations.map((a) => [a.id, a.institution])), [activeAffiliations]);

  function affCount(id: string): number {
    return collections.filter((c) => (id === 'personal' ? c.affiliationIds.length === 0 : c.affiliationIds.includes(id))).length;
  }

  const visible = useMemo(() => {
    let list = collections.filter((c) => c.lifecycleState === lifecycle);
    if (aff !== 'all') list = list.filter((c) => (aff === 'personal' ? c.affiliationIds.length === 0 : c.affiliationIds.includes(aff)));
    if (search.trim()) list = list.filter((c) => c.title.toLowerCase().includes(search.trim().toLowerCase()));
    list = [...list].sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      if (sort === 'title') return a.title.localeCompare(b.title);
      if (sort === 'size') return b.experienceCount - a.experienceCount;
      return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '');
    });
    return list;
  }, [collections, lifecycle, aff, search, sort]);

  function openMenu(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    setMenuId((cur) => (cur === id ? null : id));
  }

  // ── mutations (optimistic where cheap) ──
  function handleSave(draft: CollectionDraft) {
    const editing = modal?.collection;
    setModal(null);
    if (editing) {
      api.patch<Collection>(`/collections/${editing.id}`, { ...draft, version: editing.version })
        .then((updated) => setCollections((cs) => cs.map((c) => (c.id === updated.id ? updated : c))))
        .catch(() => showToast(t('collections.errors.failedSave'), 'error'));
    } else {
      api.post<Collection>('/collections', draft)
        .then((created) => setCollections((cs) => [created, ...cs]))
        .catch(() => showToast(t('collections.errors.failedCreate'), 'error'));
    }
  }

  function handleDuplicate(c: Collection) {
    setMenuId(null);
    api.post<Collection>(`/collections/${c.id}/duplicate`)
      .then((copy) => setCollections((cs) => [copy, ...cs]))
      .catch(() => showToast(t('collections.errors.failedDuplicate'), 'error'));
  }

  function handleArchiveToggle(c: Collection) {
    setMenuId(null);
    const next = c.lifecycleState === 'archived' ? 'active' : 'archived';
    api.patch<Collection>(`/collections/${c.id}`, { lifecycleState: next, version: c.version })
      .then((updated) => setCollections((cs) => cs.map((x) => (x.id === updated.id ? updated : x))))
      .catch(() => showToast(t('collections.errors.failedArchive'), 'error'));
  }

  function confirmDelete() {
    const c = deleteTarget;
    if (!c) return;
    setDeleteTarget(null);
    api.delete(`/collections/${c.id}?cascade=true`)
      .then(() => setCollections((cs) => cs.filter((x) => x.id !== c.id)))
      .catch(() => showToast(t('collections.errors.failedDelete'), 'error'));
  }

  const affPills: { id: string; label: string }[] = [
    { id: 'all', label: t('collections.filter.all') },
    ...activeAffiliations.map((a) => ({ id: a.id, label: a.institution })),
    { id: 'personal', label: t('collections.filter.personal') },
  ];

  return (
    <div className='flex h-screen flex-col overflow-hidden bg-ws-bg'>
      <DashboardHeader search={{ value: search, onChange: setSearch, placeholder: t('collections.filter.search') }} />
      <div className='flex flex-1 flex-col overflow-y-auto'>
        {/* Toolbar (no page title — the top nav already frames the app) */}
        <div className='flex flex-col gap-3 px-4 pt-5 pb-5 sm:flex-row sm:items-center sm:px-6 sm:pt-6'>
          {/* Affiliation filter */}
          <div className='flex flex-wrap gap-2'>
            {affPills.map((p) => (
              <button
                key={p.id}
                onClick={() => setAff(p.id)}
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${aff === p.id ? 'border-ws-primary bg-ws-primary text-white' : 'border-ws-border bg-ws-surface text-ws-muted hover:bg-ws-hover'}`}
              >
                {p.id !== 'all' && <span className='h-2 w-2 rounded-full' style={{ background: p.id === 'personal' ? '#94A3B8' : markColor(p.id) }} />}
                {p.label}
                {p.id !== 'all' && <span className='opacity-60'>{affCount(p.id)}</span>}
              </button>
            ))}
          </div>
          {/* Filter controls (grouped). On mobile portrait New Collection drops beneath these. */}
          <div className='flex items-center gap-3 sm:ml-auto'>
            <div className='inline-flex rounded-lg bg-ws-lane p-0.5'>
              {(['active', 'archived'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setLifecycle(s)}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold ${lifecycle === s ? 'bg-ws-surface text-ws-text shadow-sm' : 'text-ws-muted'}`}
                >
                  {t(s === 'active' ? 'collections.filter.active' : 'collections.filter.archived')}
                </button>
              ))}
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              className='hidden h-9 rounded-lg border border-ws-border bg-ws-control px-2 text-sm text-ws-muted sm:block'
            >
              <option value='updated'>{t('collections.sort.updated')}</option>
              <option value='title'>{t('collections.sort.title')}</option>
              <option value='size'>{t('collections.sort.size')}</option>
            </select>
          </div>
          {/* New collection — separated from the filter controls. Full width only on mobile
              portrait (below sm); content-width and non-shrinking otherwise. */}
          <Button variant='primary' onClick={() => setModal({ mode: 'create' })} className='shrink-0 max-sm:w-full'>
            {t('collections.new')}
          </Button>
        </div>

        {/* Grid */}
        <div className='px-4 pb-16 sm:px-6'>
          {loading ? (
            <p className='py-16 text-center text-sm text-ws-muted'>{t('common.loading')}</p>
          ) : error ? (
            <p className='py-16 text-center text-sm text-red-500'>{error}</p>
          ) : (
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
              {visible.map((c) => (
                <CollectionCard
                  key={c.id}
                  c={c}
                  sourceTitle={c.heritageId ? titleById.get(c.heritageId) : undefined}
                  affName={affName}
                  onOpen={() => navigate(`/app/collection/${c.id}`)}
                  onMenu={(e) => openMenu(e, c.id)}
                />
              ))}
              {visible.length === 0 && (
                <p className='col-span-full py-12 text-center text-sm text-ws-muted'>{t('collections.empty')}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Kebab menu (portaled to avoid card clipping) */}
      {menuId && createPortal(
        (() => {
          const c = collections.find((x) => x.id === menuId);
          if (!c) return null;
          const item = 'flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-ws-text hover:bg-ws-hover';
          return (
            <div ref={menuRef} style={{ top: menuPos.top, right: menuPos.right }} className={`fixed ${POPUP_LEVEL} w-52 overflow-hidden rounded-md border border-ws-border bg-ws-elevated shadow-lg`}>
              <button className={item} onClick={() => { setModal({ mode: 'edit', collection: c }); setMenuId(null); }}><Pencil className='h-4 w-4 text-ws-muted' />{t('collections.menu.edit')}</button>
              <button className={item} onClick={() => handleDuplicate(c)}><Copy className='h-4 w-4 text-ws-muted' />{t('collections.menu.duplicate')}</button>
              <button className={item} onClick={() => handleArchiveToggle(c)}>
                {c.lifecycleState === 'archived' ? <ArchiveRestore className='h-4 w-4 text-ws-muted' /> : <Archive className='h-4 w-4 text-ws-muted' />}
                {t(c.lifecycleState === 'archived' ? 'collections.menu.restore' : 'collections.menu.archive')}
              </button>
              <div className='border-t border-ws-border' />
              <button className={`${item} text-ws-warning-fg`} onClick={() => { setDeleteTarget(c); setMenuId(null); }}><Trash2 className='h-4 w-4' />{t('common.delete')}</button>
            </div>
          );
        })(),
        document.body,
      )}

      {modal && (
        <CollectionModal
          mode={modal.mode}
          initial={modal.collection}
          affiliations={activeAffiliations}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
      {deleteTarget && (
        <DeleteCollectionModal collection={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={confirmDelete} />
      )}
      {toast && <Toast message={toast.msg} type={toast.type} onDismiss={() => setToast(null)} />}
    </div>
  );
}

// ── Collection card ──────────────────────────────────────────────────────────
function CollectionCard({ c, sourceTitle, affName, onOpen, onMenu }: {
  c: Collection;
  sourceTitle?: string;
  affName: Map<string, string>;
  onOpen: () => void;
  onMenu: (e: React.MouseEvent) => void;
}) {
  const { t } = useTranslation();
  const tint = markColor(c.id);
  const modifiers = [
    c.label,
    c.governanceClass && t(`collections.governance.${c.governanceClass === 'formal' ? 'formal' : 'informal'}`),
    c.lifecycleClass && t(`collections.lifecycle.${c.lifecycleClass === 'time-boxed' ? 'timeBoxed' : 'perpetual'}`),
  ].filter(Boolean);

  return (
    <div
      onClick={onOpen}
      className={`group hover-card hover-card-raise relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-ws-border bg-ws-surface shadow-sm ${c.lifecycleState === 'archived' ? 'opacity-90 saturate-[.6]' : ''}`}
    >
      {/* Title header (top of the card) */}
      <div className='flex items-start justify-between gap-2 border-b border-ws-border px-4 py-3'>
        <h3 className='text-[15px] font-semibold leading-tight text-ws-text'>{c.title}</h3>
        <button
          onClick={onMenu}
          aria-label={t('collections.menu.aria')}
          className='-mr-1.5 -mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-ws-muted opacity-0 transition hover:bg-ws-hover group-hover:opacity-100 focus:opacity-100'
        >
          <MoreVertical className='h-4 w-4' />
        </button>
      </div>
      {/* Content zone — info and the plant mark (or real artwork) share this area */}
      <div
        className='relative flex min-h-[132px] flex-1 flex-col gap-2 overflow-hidden p-4'
        style={{ background: `radial-gradient(130% 150% at 88% 4%, color-mix(in srgb, ${tint} 16%, var(--ws-surface)) 0%, var(--ws-surface) 66%)` }}
      >
        {c.coverImage
          ? <img src={c.coverImage} alt='' className='pointer-events-none absolute bottom-0 right-0 h-full w-auto object-contain opacity-70' />
          : <CollectionMark seed={c.id} className='pointer-events-none absolute -bottom-2 -right-3 h-[120px] w-[120px]' opacity={0.55} />}
        <div className='relative flex flex-1 flex-col gap-2'>
          {modifiers.length > 0 && <p className='text-xs text-ws-muted'>{modifiers.join(' · ')}</p>}
          {c.heritageId && (
            <p className='flex items-center gap-1.5 text-xs text-ws-muted'>
              <GitFork className='h-3.5 w-3.5' style={{ color: tint }} />
              {sourceTitle ? t('collections.forkedFrom', { title: sourceTitle }) : t('collections.forkedCopy')}
            </p>
          )}
          {c.affiliationIds.length > 0 && (
            <div className='flex flex-wrap gap-1.5'>
              {c.affiliationIds.map((id) => (
                <span key={id} className='inline-flex items-center gap-1.5 rounded-full border border-ws-border bg-ws-surface/70 px-2 py-0.5 text-[11px] font-medium text-ws-muted backdrop-blur-sm'>
                  <span className='h-1.5 w-1.5 rounded-full' style={{ background: markColor(id) }} />
                  {affName.get(id) ?? t('collections.filter.personal')}
                </span>
              ))}
            </div>
          )}
          <div className='mt-auto flex items-center gap-2 pt-2 text-xs text-ws-muted'>
            <span><b className='font-semibold text-ws-text tabular-nums'>{c.experienceCount}</b> {t('collections.experiences')}</span>
            <span className='opacity-40'>·</span>
            {c.liveCount > 0
              ? <span className='font-semibold text-ws-success-fg'>{t('collections.nLive', { count: c.liveCount })}</span>
              : <span>{t('collections.noneLive')}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
